// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { css } from '@emotion/css';
import { Theme, useTheme, Fab, Badge, Tooltip } from '@mui/material';
import { BiMessageDetail } from 'react-icons/bi';

import { useMoveChat } from '@features/moves/hooks/useMoveChat';
import { useChatModal } from '@components/Chat/ChatModalManager';
import { updateMoveReadStatus } from '@features/moves/services/smsReadStatusTracker';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';

// TYPES ---------------------------------------------------------------- //

interface MoveFloatingChatProps {
  moveId?: number;
  consumerPickup?: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const MoveFloatingChat = ({ moveId, consumerPickup }: MoveFloatingChatProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const isSelfManaged = useValetSelfManaged();

  // Use shared hook for messages (no polling - global subscription handles updates)
  const { messages } = useMoveChat(moveId, consumerPickup);

  // Check if there are unread messages
  const hasUnread = React.useMemo(() => {
    if (!moveId || messages.length === 0) return false;
    
    // Update the read status and get the result
    const status = updateMoveReadStatus(moveId, messages);
    return status.hasUnread;
  }, [moveId, messages]);

  // Use global chat modal
  const { openChat } = useChatModal();

  // Don't render for a non-self-managed customer, or without consumer pickup or a moveId.
  if (!isSelfManaged || !consumerPickup || !moveId) {
    return null;
  }

  const handleOpenModal = React.useCallback(() => {
    openChat(moveId, consumerPickup);
  }, [openChat, moveId, consumerPickup]);

  return (
    <div className={cls.floatingButtonContainer}>
      <Badge
        variant='dot'
        color='error'
        invisible={!hasUnread}
        sx={{
          '& .MuiBadge-badge': {
            right: 0,
            top: 0,
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            animation: hasUnread ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                opacity: 1,
              },
              '50%': {
                transform: 'scale(1.3)',
                opacity: 0.7,
              },
              '100%': {
                transform: 'scale(1)',
                opacity: 1,
              },
            },
          },
        }}
      >
        <Tooltip 
          title={hasUnread ? "You have unread messages" : "Open chat"}
          placement="left"
        >
          <Fab
            color='primary'
            onClick={handleOpenModal}
            className={cls.floatingButton}
            size='large'
            sx={{
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              '&:hover': {
                boxShadow: '0 6px 25px rgba(0,0,0,0.25)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <BiMessageDetail size={24} />
          </Fab>
        </Tooltip>
      </Badge>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  floatingButtonContainer: css`
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 1000;
  `,

  floatingButton: css`
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    will-change: transform, box-shadow;

    &:hover {
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
    }
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default React.memo(MoveFloatingChat);
